<template>
  <div>
    <h4
      class="ml-2"
      data-cy="heading"
    >
      {{ headingText }}
    </h4>
    <b-table
      :fields="fields"
      data-cy="table-seller-offers"
      :items="offers"
      :per-page="pageSize"
      :current-page="currentPage"
      thead-class="text-nowrap"
      tbody-tr-class="text-nowrap"
      primary-key="id"
    >
      <template #cell(status)="row">
        <b-badge
          tag="div"
          class="w-100 py-1"
          :variant="getBadgeVariant(row.item)"
        >
          {{ row.item.isDisabled ? 'Disabled' : (statusNames[row.item.status] || row.item.status) }}
        </b-badge>
      </template>

      <template #cell(createdAt)="row">
        {{ row.item.hasAppliedToOffer ? dateFormat(row.item.createdAt, 'MMM DD, YYYY') : '' }}
      </template>

      <template #cell(name)="row">
        <div class="d-flex align-items-center">
          <span>{{ row.item.name }}</span>
        </div>
      </template>

      <template #cell(category.name)="row">
        <div class="text-nowrap">
          {{ row.item.category.name }}
        </div>
      </template>

      <template #cell(language.name)="row">
        <div class="text-nowrap">
          {{ row.item.language.name }}
        </div>
      </template>

      <template #cell(qualityScore)="row">
        <div class="text-nowrap">
          {{
            dashIfNotAvailable(
              () => `${row.item.qualityScore.toFixed(2)} / 10`
            )
          }}
        </div>
      </template>

      <template #cell(liveConcurrency)="row">
        <div class="text-nowrap">
          {{
            renderFractionInUSLocale(
              row.item.liveConcurrency,
              row.item.liveConcurrencyCap
            )
          }}
        </div>
      </template>

      <template #cell(dailyConcurrency)="row">
        <div class="text-nowrap">
          {{
            renderFractionInUSLocale(
              row.item.dailyConcurrency,
              row.item.dailyConcurrencyCap
            )
          }}
        </div>
      </template>

      <template #cell(weeklyConcurrency)="row">
        <div class="text-nowrap">
          {{
            renderFractionInUSLocale(
              row.item.weeklyConcurrency,
              row.item.weeklyConcurrencyCap
            )
          }}
        </div>
      </template>

      <template #cell(monthlyConcurrency)="row">
        <div class="text-nowrap">
          {{
            renderFractionInUSLocale(
              row.item.monthlyConcurrency,
              row.item.monthlyConcurrencyCap
            )
          }}
        </div>
      </template>

      <template #cell(rateLimit)="row">
        <div class="text-nowrap">
          {{ row.item.rateLimit ? `${renderNumberInUSLocale(row.item.rateLimit)} PPM ` : '-' }}
        </div>
      </template>

      <template #cell(bidTTLInSeconds)="row">
        <div class="text-nowrap">
          {{
            row.item.bidTTLInSeconds
              ? `${renderNumberInUSLocale(row.item.bidTTLInSeconds)} seconds `
              : '-'
          }}
        </div>
      </template>

      <template #cell(actions)="row">
        <SellerAppliedOfferActions
          v-if="!row.item.canApplyToOffer || row.item.isRejected"
          :offer-registration="row.item"
          :class="{'mr-2': row.item.isRejected}"
          :show-ringba-integration-actions="showRingbaIntegrationActions"
          @showApplicationRequestDetails="emitShowApplicationRequestDetails"
          @showBiddingUrl="emitShowBiddingUrl"
          @showWithdrawModal="emitShowWithdrawModal"
          @showRejectionModal="emitShowRejectionModal"
        />

        <b-button
          v-if="row.item.canApplyToOffer"
          variant="outline-info"
          size="sm"
          @click="emitShowApplyToRunModal(row.item)"
        >
          Apply to Run
          <font-awesome-icon

            class="ml-2"
            data-cy="icon-pause"
            :icon="['fas', 'play']"
            :style="{ cursor: 'pointer' }"
          />
        </b-button>
      </template>
    </b-table>

    <div class="d-flex justify-content-center">
      <b-pagination
        v-model="currentPage"
        size="sm"
        :total-rows="offers.length"
        :per-page="pageSize"
      />
    </div>
  </div>
</template>

<script>
import SellerAppliedOfferActions from './SellerAppliedOfferActions.vue'
import fractionMixin from '@/mixins/numbersInLocaleMixin'
import dateFormatMixin from '@/mixins/dateFormatMixin'

const statusNames = {
  Active: 'Available',
  ManuallyBlocked: 'Removed'
}

export default {
  name: 'SellerOfferList', 

  components: {
    SellerAppliedOfferActions,
  },

  mixins: [fractionMixin, dateFormatMixin],

  props: {
    offers: {
      type: Array,
      default() {
        return []
      }
    },
    showRingbaIntegrationActions: {
      type: Boolean,
      default: false
    },
    headingText: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      pageSize: 15,
      currentPage: 1,
      statusNames: Object.freeze(statusNames),
      fields: [
        {
          key: 'status',
          label: 'Status',
          tdAttr: value => {
            return {
              'data-cy': 'status-badge',
              'data-cy-status': value
            }
          },
          sortable: true
        },
        { key: 'createdAt', label: 'Applied', sortable: true },
        { key: 'name', label: 'Name', sortable: true, searchable: true },
        { key: 'category.name', label: 'Category', sortable: true, searchable: true },
        { key: 'language.name', label: 'Language', sortable: true, searchable: true },
        { key: 'allowedTrafficSourceType', label: 'Traffic Source Type', sortable: true, searchable: true },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },

  methods: {
    getBadgeVariant(offer) {
      const status = offer.isDisabled ? 'Disabled' : offer.status

      const statusToVariantMap = {
        Active: 'primary',
        Disabled: 'light',
        Pending: 'secondary',
        Approved: 'success',
        Rejected: 'danger',
        NotEligible: 'danger',
        Withdrawn: 'warning',
        ManuallyBlocked: 'info'
      }

      return statusToVariantMap[status]
    },

    emitShowApplicationRequestDetails(offerRegistration) {
      this.$emit('showApplicationRequestDetails', offerRegistration)
    },

    emitShowBiddingUrl(offerRegistration) {
      this.$emit('showBiddingUrl', offerRegistration)
    },

    emitShowWithdrawModal(offerRegistration) {
      this.$emit('showWithdrawModal', offerRegistration)
    },

    emitShowRejectionModal(offerRegistration) {
      this.$emit('showRejectionModal', offerRegistration)
    },

    emitShowApplyToRunModal(offerRegistration) {
      this.$emit('showApplyToRunModal', offerRegistration)
    }
  }
}
</script>

<style lang="scss">
</style>